import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import type { UrlStateAdapter } from './filters/urlAdapter'
import { useLocalePath } from '../layout/LanguageRoute'
import SearchAutocompleteBox from './SearchAutocompleteBox'
import type { Suggestion } from './SearchAutocompleteBox'

export default function ProductSearch({ adapter }: { adapter?: UrlStateAdapter } = {}) {
  if (adapter) {
    return <ProductSearchInner adapter={adapter} />
  }
  return <ProductSearchReactRouter />
}

function ProductSearchReactRouter() {
  const [rrParams, setRrParams] = useSearchParams()
  const adapter: UrlStateAdapter = {
    searchParams: rrParams,
    setSearchParams: (next) => setRrParams(next),
  }
  return <ProductSearchInner adapter={adapter} />
}

function ProductSearchInner({ adapter }: { adapter: UrlStateAdapter }) {
  const { searchParams, setSearchParams } = adapter
  const [value, setValue] = useState(searchParams.get('search') ?? searchParams.get('q') ?? '')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchParamsRef = useRef(searchParams)
  // eslint-disable-next-line react-hooks/refs
  searchParamsRef.current = searchParams
  const navigate = useNavigate()
  const lp = useLocalePath()

  useEffect(() => {
    setValue(searchParams.get('search') ?? searchParams.get('q') ?? '')
  }, [searchParams])

  function applySearch(v: string) {
    if (timerRef.current) clearTimeout(timerRef.current)
    const next = new URLSearchParams(searchParamsRef.current)
    if (v.trim()) {
      next.set('search', v.trim())
    } else {
      next.delete('search')
    }
    next.delete('page')
    setSearchParams(next)
  }

  function handleChange(v: string) {
    setValue(v)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => applySearch(v), 300)
  }

  function handleSelectSuggestion(s: Suggestion) {
    if (timerRef.current) clearTimeout(timerRef.current)
    navigate(lp(`/product/${s.slug}`))
  }

  return (
    <div className="relative w-full max-w-md">
      <SearchAutocompleteBox
        value={value}
        onChange={handleChange}
        onSubmit={applySearch}
        onSelectSuggestion={handleSelectSuggestion}
        inputClassName="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-neutral-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
    </div>
  )
}
